import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import {TrackingProvider, TrackingContext} from '@vrbo/react-event-tracking';
import edapWrapper from '@homeaway/edap-wrapper';

/**
 * Provides the EDAP implementation of trigger to the TrackingProvider.
 */
class EdapProvider extends PureComponent {
    static propTypes = {
        /** An object of event specific fields where the event name is the key and the value is an object of field key/value pairs for the event. Event specific values will be merged with defaults from the `fields` property. */
        eventFields: PropTypes.objectOf(PropTypes.objectOf(PropTypes.string)),
        /** An object of event specific options where the event name is the key and the value is an object of option key/value pairs for the event. Event specific values will be merged with defaults from the `options` property. */
        eventOptions: PropTypes.objectOf(PropTypes.objectOf(PropTypes.string)),
        /** Object of string values that represents the default fields to apply to all events within this context. */
        fields: PropTypes.objectOf(PropTypes.string),
        /** The trigger options. */
        options: PropTypes.objectOf(PropTypes.string),
        /** When true, overwrites the current context with specified properties. Default is to merge instead of overwrite. */
        overwrite: PropTypes.bool
    };

    static defaultProps = {
        overwrite: false
    }

    /**
     * The implementation for using EDAP to "trigger" an event.
     */
    trigger(name, fields = {}, options = {}) {
        if (!name) {
            throw new TypeError('event is a required parameter');
        }

        edapWrapper().push({
            name,
            data: fields,
            options
        });

        return {
            name,
            fields,
            options
        };
    }

    render() {
        const {eventFields, eventOptions, fields, children, options, overwrite} = this.props;

        // Wrapping the TrackingProvider in a Consumer so that a trigger function
        // specified higher in the DOM chain will be used instead of the one defined
        // by this class. The main use case for this is to allow EdapTestProvider
        // to override the trigger of children for event validation.
        return (
            <TrackingContext.Consumer>
                {({_data}) => {
                    const trigger = _data.trigger || this.trigger;
                    return (
                        <TrackingProvider
                            eventFields={eventFields}
                            eventOptions={eventOptions}
                            fields={fields}
                            options={options}
                            overwrite={overwrite}
                            trigger={trigger}
                        >
                            {children}
                        </TrackingProvider>
                    );
                }}
            </TrackingContext.Consumer>
        );
    }
}

export default EdapProvider;
